"use client"

import Text from "@/components/common/Typography/Text"
import Title from "@/components/common/Typography/Title"
import SuccessIcon from "@/components/icons/SuccessIcon"
import { Button, Card, Col, Divider, Row, Spin } from "antd"
import { FC, Fragment, JSX, useEffect, useMemo, useState } from "react"
import { useParams, useRouter, useSearchParams } from "next/navigation"
import { useAppDispatch, useAppSelector } from "@/lib/redux/hooks"
import ErrorIcon from "@/components/icons/ErrrorIcon"

import WarningIcon from "@/components/icons/WarningIcon"
import FlightBooking from "./FlightBooking"
import HotelBooking from "./HotelBooking"
import { parseJson } from "@/utils/safeParse/parseJson"
import { getOrderDetail } from "@/utils/booking/getOrderDetail"
import { TypeSegment } from "@/lib/redux/features/flight/type"
import PageLoading from "@/components/common/PageLoading"
import OfflineSnapshotBanner from "@/components/common/OfflineSnapshotBanner"
import { bookingActions } from "@/lib/redux/features/booking/reduce"
import { useTERouter } from "@/hooks/app/useTERouter"
import TourBooking from "./TourBooking"
import { useTranslation } from "react-i18next"
import { getFlightPnrDisplayItems, getIsPartialSuccess } from "@/utils/booking/getFlightPnrDisplayItems"
import { isGalileoSystem } from "@/utils/flight/isGalileoSystem"
import { track, TrackingEventName } from "@/lib/tracking"

let timeout: any

export const PROMOTION_AIRLINE = 'QH'

type BookingResultProps = {
  needToRefeshData?: boolean
}

const waitingTimes: any = {
  0: 8000,
  1: 6000,
  2: 3000
}

let timeRefesh = 0

const BookingResult: FC<BookingResultProps> = ({ needToRefeshData = true }) => {
  const params = useParams()
  const searchParams = useSearchParams()
  const [fetching, setFetching] = useState<boolean>(false)
  const order = useAppSelector(state => state.booking.order_detail)
  const orderDetailOfflineAt = useAppSelector(state => state.booking.orderDetailOfflineAt)
  const metaData = parseJson(order.data?.metaData) || []
  const itinData = parseJson(order.data?.itinData) || []
  const isFlight = order.data?.productType === 'FLIGHT'
  const contact = isFlight
    ? (metaData[0]?.contact || {})
    : metaData?.booking?.customerDetail || metaData?.customerDetail || {}
  const router = useTERouter()
  const { t } = useTranslation()

  const isHold = order?.data?.bookingStatus === 'HOLD'
  const isNewOrder = order?.data?.orderStatus === 'NEW'
  const isCancelPayment = order?.data?.paymentStatus === 'CANCEL'
  const isCancel = order?.data?.bookingStatus === 'CANCEL' ||
    order?.data?.bookingStatus === 'FAIL' ||
    isCancelPayment

  const isPromotion = isFlight ? order?.data?.isVJPromotion && itinData?.some((segment: TypeSegment) => segment.airline === PROMOTION_AIRLINE) : false
  const is1G = !isFlight ? false : metaData.some((m: any) => m.fareData?.some((f: any) => isGalileoSystem(f?.system)))

  const isFlightPromotionAndSucceed = isFlight
    && isPromotion
    && order?.data?.paymentStatus === 'PAID'
    && (order?.data?.bookingStatus === 'ACTIVE' || order?.data?.bookingStatus === 'HOLD')
  const pnrItems = useMemo(() => {
    return getFlightPnrDisplayItems({
      pnrNumber: order?.data?.pnrNumber,
      tickets: order?.data?.tickets,
      orderStatus: order?.data?.orderStatus,
      bookingStatus: order?.data?.bookingStatus,
      ticketStatus: order?.data?.ticketStatus
    })
  }, [order?.data?.pnrNumber, order?.data?.tickets, order?.data?.orderStatus, order?.data?.bookingStatus, order?.data?.ticketStatus])
  const isPartialSuccess = isFlight && getIsPartialSuccess(pnrItems)

  const dispatch = useAppDispatch()

  const refetchOrderDetailInterval = () => {
    if (!needToRefeshData) {
      return;
    }

    if (timeout) {
      clearTimeout(timeout)
    }
    timeout = setTimeout(async () => {
      const phone = searchParams.get('phone')
      const res = await getOrderDetail({ orderCode: params?.orderCode, phoneNumber: phone })
      const hasPnr = Boolean(
        String(res?.pnrNumber || '').trim() ||
        (Array.isArray(res?.tickets) && res.tickets.some((ticket: any) => String(ticket?.pnrNumber || '').trim())),
      )

      if (res?.bookingStatus !== 'HOLD' || hasPnr || timeRefesh >= 3) {
        setFetching(false)
      } else {
        timeRefesh++
        refetchOrderDetailInterval()
      }
    }, waitingTimes[timeRefesh] || 3000)
  }

  useEffect(() => {
    if (params?.orderCode) {
      const phone = searchParams.get('phone')
      timeRefesh = 0
      setFetching(true)
      getOrderDetail({ orderCode: params?.orderCode, phoneNumber: phone })
        .then((res) => {
          if (res?.paymentStatus !== 'CANCEL' && res?.bookingStatus === 'HOLD' && !!needToRefeshData && !isFlightPromotionAndSucceed) {
            refetchOrderDetailInterval()
          } else {
            if (timeout) {
              clearTimeout(timeout)
            }
            setFetching(false)
          }
        }).catch(() => {
          setFetching(false)
        })
    }
  }, [params?.orderCode, needToRefeshData, isFlightPromotionAndSucceed])

  useEffect(() => {
    dispatch(bookingActions.gotoBookingResult())
    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
  }, [])

  useEffect(() => {
    const orderCode = String(params?.orderCode || '')
    if (!orderCode || typeof window === 'undefined' || pnrItems.length > 0) return

    const reloadKey = `te_order_result_delayed_reload_${orderCode}`
    if (sessionStorage.getItem(reloadKey)) return

    const reloadTimer = window.setTimeout(() => {
      sessionStorage.setItem(reloadKey, '1')
      window.location.reload()
    }, 20000)

    return () => window.clearTimeout(reloadTimer)
  }, [params?.orderCode, pnrItems.length])

  // DOP-925: {flight|hotel|tour}_payment_success — khi order đã PAID.
  // Guard bằng sessionStorage để F5/quay lại trang kết quả không bắn trùng.
  const paidOrderCode = order?.data?.paymentStatus === 'PAID' ? order?.data?.orderCode : null
  useEffect(() => {
    if (!paidOrderCode || typeof sessionStorage === 'undefined') return
    const trackedKey = `te_tracked_payment_${paidOrderCode}`
    if (sessionStorage.getItem(trackedKey)) return
    sessionStorage.setItem(trackedKey, '1')

    const productType = `${order?.data?.productType || ''}`.toLowerCase()
    const eventName = ['flight', 'hotel', 'tour'].includes(productType)
      ? `${productType}_payment_success` as TrackingEventName
      : null
    if (!eventName) return

    track(eventName, {
      order_id: paidOrderCode,
      amount: parseFloat(`${order?.data?.totalAmount ?? 0}`.replace(/,/g, '')) || 0,
    })
  }, [paidOrderCode])

  const buttonBackToHome = (
    <Row justify='center' className="booking__result--box">
      <Button
        size='large'
        type='primary'
        onClick={() => router.push('/')}
      >
        {t("common.back_home")}
      </Button>
    </Row>
  )

  if (
    Object.keys(order?.data || {}).length <= 0 &&
    !order?.error
  ) {
    return (
      <PageLoading />
    )
  }

  const title: any = {
    CONFIRM_PAID_ACTIVE: t("booking.booking_successful_message"),
    CONFIRM_PAID_FAIL: t("booking.booking_fail_message_1", { hotline: process.env.NEXT_PUBLIC_COMPANY_PHONE }),
    CONFIRM_PAID_HOLD: t("booking.booking_issue_message_1"),
    CONFIRM_PAID_HOLD_FAIL: t("booking.booking_issue_message_2"),
    HOLD_PENDING_HOLD: t("booking.booking_payment_message_1"),
    CANCEL_PAID_CANCEL: t("booking.booking_cancelled_message_1"),
    FAIL_FAIL_HOLD: t("booking.booking_payment_failed_message_1"),
    NEW_CANCEL_CANCEL: t("booking.booking_payment_cancelled_message_1"),
    NEW_PENDING_FAIL: t("booking.booking_hold_failed_message_1"),
    NEW_PENDING_HOLD: t("booking.booking_request_support_message_1"),
    HOLD_CANCEL_HOLD: t("booking.booking_payment_cancelled_message_1"),
  }

  const icon: any = {
    CONFIRM_PAID_ACTIVE: <SuccessIcon className="booking__result--icon" />,
    CONFIRM_PAID_FAIL: <ErrorIcon className="booking__result--icon" />,
    CONFIRM_PAID_HOLD: <WarningIcon className="booking__result--icon" />,
    HOLD_PENDING_HOLD: <WarningIcon className="booking__result--icon" />,
    CANCEL_PAID_CANCEL: <ErrorIcon className="booking__result--icon" />,
    FAIL_FAIL_HOLD: <ErrorIcon className="booking__result--icon" />,
    NEW_PENDING_FAIL: <ErrorIcon className="booking__result--icon" />,
    NEW_CANCEL_CANCEL: <ErrorIcon className="booking__result--icon" />,
    HOLD_CANCEL_HOLD: <ErrorIcon className="booking__result--icon" />,
    CONFIRM_PAID_HOLD_FAIL: <WarningIcon className="booking__result--icon" />,
    NEW_PENDING_HOLD: <WarningIcon className="booking__result--icon" />,
  }

  const subTitle: any = {
    CONFIRM_PAID_ACTIVE: t("booking.booking_successful_note", { platform: process.env.NEXT_PUBLIC_PLATFORM_NAME || "Triphubs", email: contact?.email }),
    CONFIRM_PAID_HOLD: t("booking.booking_issue_note_1"),
  }

  const status = [order?.data?.orderStatus, order?.data?.paymentStatus, order?.data?.bookingStatus, order?.data?.ticketStatus].filter(status => !!status).join('_')
  const displayTitle = isPartialSuccess
    ? t("booking.booking_partial_success_message")
    : (isFlightPromotionAndSucceed || (is1G && order?.data?.paymentStatus === 'PAID')
      ? t("booking.booking_successful_message")
      : title[status] || t("booking.booking_random_status_message", { hotline: process.env.NEXT_PUBLIC_COMPANY_PHONE }))
  const displaySubTitle = isPartialSuccess
    ? t("booking.booking_partial_success_note", { hotline: process.env.NEXT_PUBLIC_COMPANY_PHONE })
    : subTitle[status]

  if (order.error) {
    return (
      <>
        <Row justify='center' align='top' className="booking__result">
          <Col className="booking__result--box" span={24}>
            <Row>
              <Col span={24}>
                <Card>
                  <Row justify='center'>
                    <Col>
                      <ErrorIcon className="booking__result--icon" />
                    </Col>
                  </Row>
                  <Row justify='center'>
                    <Title level={3} style={{ marginTop: '1rem' }}>
                      {t("order.not_found")}
                    </Title>
                  </Row>
                  <Row justify='center'>
                    <Text type='secondary'>
                      {t("order.not_found_message")}
                    </Text>
                  </Row>
                </Card>
              </Col>
            </Row>
            {buttonBackToHome}
          </Col>
        </Row>
      </>
    )
  }

  return (
    <Spin spinning={isHold && needToRefeshData && fetching}>
      <Row justify='center' className="booking__result">
        <Col className="booking__result--box" span={24}>
          {!!orderDetailOfflineAt && <OfflineSnapshotBanner savedAt={orderDetailOfflineAt} />}
          <Row>
            <Col span={24}>
              <Card size="small" style={{ maxWidth: "100vw" }}>
                <Row justify='center'>
                  <Col>
                    {isPartialSuccess
                      ? <WarningIcon className="booking__result--icon" />
                      : isFlightPromotionAndSucceed || (is1G && order?.data?.paymentStatus === 'PAID') ?
                      <SuccessIcon className="booking__result--icon" />
                      : icon[status] || <ErrorIcon className="booking__result--icon" />}
                  </Col>
                </Row>
                <Row justify='center'>
                  <Title level={3} style={{ marginTop: '1rem', textAlign: 'center' }}>
                    {displayTitle}
                  </Title>
                </Row>
                {!!displaySubTitle && (
                  <Row justify='center'>
                    <Text type='secondary' style={{ textAlign: 'center' }}>
                      {displaySubTitle}
                    </Text>
                  </Row>
                )}
                <Row gutter={[0, 15]} style={{ marginBlock: '1.5rem' }}>
                  <Col span={24}>
                    <Card
                      size='small'
                      className="booking__result--bookingcode"
                    >
                      <Row justify='space-between' align='middle'>
                        <Col>
                          <Text>{t("common.order_code")}</Text>
                        </Col>
                        <Col>
                          <Text copyable strong className="booking__result--bookingcode-btncopy">{order?.data?.orderCode}</Text>
                        </Col>
                      </Row>
                    </Card>
                  </Col>
                </Row>
                {order.data?.productType === 'FLIGHT' && (<FlightBooking showExpiredDay />)}
                {order.data?.productType === 'HOTEL' && (<HotelBooking />)}
                {order.data?.productType === 'TOUR' && (<TourBooking />)}
              </Card>
            </Col>
          </Row>
          {buttonBackToHome}
        </Col>
      </Row>
    </Spin>
  )
}

export default BookingResult